import React, { useEffect, useMemo, useState } from 'react';
import { ScrollText } from 'lucide-react';
import { toast } from 'sonner';

import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { auditApi, usersApi } from '../lib/api';

function formatTimestamp(value) {
  if (!value) return '—';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

export default function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [eventType, setEventType] = useState('all');
  const [actorId, setActorId] = useState('all');
  const [dateFrom, setDateFrom] = useState(() =>
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
  );
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [page, setPage] = useState(1);
  const pageSize = 15;

  const loadAudit = async () => {
    setLoading(true);
    setError('');
    try {
      const [auditResponse, usersResponse] = await Promise.all([
        auditApi.list({
          start_date: dateFrom,
          end_date: dateTo,
          page: 1,
          page_size: 500,
          ...(actorId !== 'all' ? { actor_id: actorId } : {}),
          ...(eventType !== 'all' ? { event_type: eventType } : {}),
        }),
        usersApi.list({ page: 1, page_size: 200 }).catch(() => ({ data: { items: [] } })),
      ]);
      setLogs(auditResponse.data?.items || []);
      setUsers(usersResponse.data?.items || []);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to load audit logs.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAudit();
  }, [dateFrom, dateTo, actorId, eventType]);

  useEffect(() => {
    setPage(1);
  }, [dateFrom, dateTo, actorId, eventType]);

  const eventOptions = useMemo(
    () => [...new Set(logs.map((log) => log.action).filter(Boolean))].sort(),
    [logs]
  );
  const paginatedLogs = useMemo(
    () => logs.slice((page - 1) * pageSize, page * pageSize),
    [logs, page]
  );

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 p-6 text-slate-300" data-testid="audit-logs-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Audit Logs</h1>
        <p className="mt-2 text-slate-500">
          Immutable administrative activity records with actor, action, target, and reason.
        </p>
      </div>

      <Card className="border-slate-700 bg-slate-800 shadow-lg">
        <CardHeader className="pb-0">
          <CardTitle className="text-white">Log Explorer</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <Select value={eventType} onValueChange={setEventType}>
              <SelectTrigger className="border-slate-600 bg-slate-700 text-white">
                <SelectValue placeholder="Event type" />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-800 text-slate-200">
                <SelectItem value="all">All Events</SelectItem>
                {eventOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={actorId} onValueChange={setActorId}>
              <SelectTrigger className="border-slate-600 bg-slate-700 text-white">
                <SelectValue placeholder="Actor" />
              </SelectTrigger>
              <SelectContent className="border-slate-700 bg-slate-800 text-slate-200">
                <SelectItem value="all">All Actors</SelectItem>
                {users.map((user) => (
                  <SelectItem key={user.id} value={user.id}>
                    {user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className="rounded-lg border border-slate-600 bg-slate-700 px-3 py-2 text-white outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className="rounded-lg border border-slate-600 bg-slate-700 px-3 py-2 text-white outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error ? (
            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-300">
              {error}
            </div>
          ) : null}

          {loading ? (
            <LoadingSkeleton type="table" />
          ) : paginatedLogs.length ? (
            <DataTable
              columns={[
                {
                  key: 'ts',
                  label: 'Timestamp',
                  render: (value) => formatTimestamp(value),
                },
                {
                  key: 'actor_email',
                  label: 'Actor',
                  render: (value, row) => value || row.actor_user_id,
                },
                {
                  key: 'action',
                  label: 'Event Type',
                  render: (value) => String(value || '').replace(/_/g, ' '),
                },
                { key: 'target_type', label: 'Resource Type' },
                { key: 'target_id', label: 'Resource ID' },
                {
                  key: 'reason',
                  label: 'Reason',
                  render: (value) => value || '—',
                },
              ]}
              data={paginatedLogs}
              loading={loading}
              emptyMessage="No audit log entries match the current filters."
              pagination={{
                page,
                pageSize,
                total: logs.length,
                onPageChange: setPage,
              }}
            />
          ) : (
            <EmptyState
              icon={<ScrollText className="h-8 w-8" />}
              title="No audit logs"
              description="No audit entries were returned for the selected filters."
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
